import { inject } from '@angular/core';
import { HttpEvent, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

import { environment } from 'src/environments/environment';
import { Logger } from '../services/logger/logger.service';
import { CredentialsService } from '../authentication/services/credentials.service';
import { TokenRefreshService } from '../authentication/services/token-refresh.service';
import { ClientContextService } from '../services/client/client-context.service';

const LOGGER = new Logger('ErrorHandlerInterceptor');

export function apiAuthenticationInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    const credentialsService = inject(CredentialsService);
    const tokenRefreshService = inject(TokenRefreshService);

    // ------------------------------------------------------------------------
    // BYPASS (La sécurité anti-boucle infinie)
    // On n'intercepte PAS les routes de login, de refresh, ou d'inscription.
    // ------------------------------------------------------------------------
    if (
        // Login et refresh token (dans le cas où le refresh token est mort, pour éviter de boucler)
        request.url.includes('/token/') ||
        // Routes d'inscription (qui utilisent une pré-authentification spéciale, sans Bearer mais avec un header PreAuth)
        request.url.includes('/registration/') &&
        // Exclusion de la route de changement d'adresse email qui nécessite d'être authentifié
        !request.url.includes('register-email') &&
        // Exclusion de la route de changement de mot de passe qui nécessite d'être authentifié
        !request.url.includes('change-password') ||
        // Si le header d'Authorization commence déjà par "PreAuth ", on considère que c'est une requête d'inscription qui ne doit pas être modifiée
        request.headers.get('Authorization')?.startsWith('PreAuth ')
    ) {
        // On laisse passer la requête telle quelle, sans ajouter de Bearer
        // et sans écouter les erreurs 401 pour ne pas boucler.
        return next(request);
    }

    // Fonction utilitaire pour cloner et injecter le token
    const addToken = (req: HttpRequest<unknown>, token: string) => {
        return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
    };

    // 1. Ajouter le header initialement si on a un token
    if (credentialsService.credentials?.access) {
        request = addToken(request, credentialsService.credentials.access);
    }

    // 2. Traitement de la requête et interception des erreurs
    return next(request).pipe(
        catchError((error: any) => {
            // On intercepte les 401
            if (error instanceof HttpErrorResponse && (error.status === 401)) {
                // Le refresh token est dans le HttpOnly cookie, envoyé automatiquement.
                // Le service met en commun la demande : si un rafraîchissement est
                // déjà en vol, cette requête l'attend au lieu d'en lancer un second.
                // En cas d'échec, il ferme la session et rouvre la page de connexion.
                return tokenRefreshService.refresh$().pipe(
                    // On rejoue la requête bloquée
                    switchMap(token => next(addToken(request, token)))
                );
            }

            // Si c'est une autre erreur (500, 404...), on la laisse passer
            return throwError(() => error);
        })
    );
}

export function apiClientInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    // Le dossier vient de l'URL depuis #173, via `ClientContextService` — et non
    // plus d'une clé de `localStorage` partagée par tous les onglets.
    const clientId = inject(ClientContextService).clientId;
    if (clientId !== null) {
        request = request.clone({ headers: request.headers.append('Context-client', clientId.toString()) });
    }
    return next(request);
}

export function apiContentTypeInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    // Si l'entête content-type n'est pas défini, alors on le rajoute
    if (!request.headers.has('Content-Type')) {
        request = request.clone({ headers: request.headers.append('Content-Type', 'application/json') });
    }
    return next(request);
}

export function apiPrefixInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    if (!/^(http|https):/i.test(request.url)) {
        request = request.clone({ url: environment.serverUrl + request.url });
    }
    return next(request);
}

export function apiDeviceIdInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {

    // Ajouter le token FCM pour les notifications push web (même pattern que mobile : X-Device-Token)
    const fcmToken = localStorage.getItem('fcm_device_token');
    if (!!fcmToken) {
        request = request.clone({ headers: request.headers.append('x-device-token', fcmToken) });
    }

    return next(request);
}

export function errorHandlerInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    return next(request).pipe(catchError(error => errorHandler(error)));

    function errorHandler(response: HttpEvent<any>): Observable<HttpEvent<any>> {
        if (!environment.production) {
            // Do something with the error
            LOGGER.error('Request error', response);
        }
        throw response;
    }
}